import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { ChevronLeft, ChevronRight, CalendarDays, CalendarRange, CalendarDays as CalendarIcon, List as ListIcon, Cable, NotebookPen, X } from "lucide-react";
import { NoteComposer } from "./NotesGlobal";

const EVENT_COLORS = {
  training: "pm-pill-blue", trial: "pm-pill-amber", media: "pm-pill-slate",
  match: "pm-pill-green", contract_deadline: "pm-pill-red",
  transfer_window: "pm-pill-amber", renewal: "pm-pill-green", other: "pm-pill-slate",
};

// Per-client palette — cycled so each player keeps a consistent colour across the week grid
const CLIENT_PALETTE = [
  "#10b981", "#3b82f6", "#f59e0b", "#a855f7", "#ec4899",
  "#0ea5e9", "#f97316", "#22c55e", "#ef4444", "#14b8a6",
];
function clientColor(idx) {
  return CLIENT_PALETTE[idx % CLIENT_PALETTE.length];
}

function startOfWeek(d) {
  const out = new Date(d);
  const day = out.getDay(); // 0=Sun
  const diff = (day === 0 ? -6 : 1) - day; // Monday-start
  out.setDate(out.getDate() + diff);
  out.setHours(0, 0, 0, 0);
  return out;
}
function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}
function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function monthGridDays(monthStart) {
  // Build a Mon-Sun 6-row grid (42 days) that covers the entire visible
  // month with leading + trailing days from neighbouring months greyed out.
  const first = startOfWeek(monthStart);
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

export default function ScheduleGlobal() {
  const [clients, setClients] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [view, setView] = useState("month"); // "month" | "week" | "list"
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [monthStart, setMonthStart] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(new Set()); // selected client ids; empty = all
  const [matchNoteFor, setMatchNoteFor] = useState(null); // event being noted

  useEffect(() => {
    (async () => {
      const cs = (await api.get("/clients")).data || [];
      setClients(cs);
      const evs = [];
      for (const c of cs) {
        const r = await api.get(`/clients/${c.id}/events`);
        (r.data || []).forEach((e) => evs.push({ ...e, client: c }));
      }
      evs.sort((a, b) => new Date(a.start) - new Date(b.start));
      setAllEvents(evs);
    })();
  }, []);

  const filtered = useMemo(() => {
    if (selected.size === 0) return allEvents;
    return allEvents.filter((e) => selected.has(e.client.id));
  }, [allEvents, selected]);

  const upcoming = filtered.filter((e) => new Date(e.start) >= new Date());

  // Group filtered events into the visible week (7 columns)
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 7);
  const weekEvents = filtered.filter((e) => {
    const t = new Date(e.start).getTime();
    return t >= weekStart.getTime() && t < weekEnd.getTime();
  });
  const eventsByDay = weekDays.map((d) => weekEvents.filter((e) =>
    new Date(e.start).toDateString() === d.toDateString()
  ));

  // Deep-link: when the dashboard calendar widget routes us here with
  // `?open=<event.id>`, auto-switch to list view, scroll the matching row
  // into view, and ring-highlight it for a few seconds. URL is cleaned so a
  // refresh doesn't re-trigger the animation.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState(null);
  const eventRefs = useRef({});
  useEffect(() => {
    const target = searchParams.get("open");
    if (!target) return;
    if (allEvents.length === 0) return;
    setView("list");
    requestAnimationFrame(() => {
      const node = eventRefs.current[target];
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightId(target);
        const next = new URLSearchParams(searchParams);
        next.delete("open");
        setSearchParams(next, { replace: true });
        setTimeout(() => setHighlightId(null), 3500);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEvents]);

  const toggle = (cid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };

  const goWeek = (delta) => setWeekStart((d) => addDays(d, 7 * delta));
  const goToday = () => {
    setWeekStart(startOfWeek(new Date()));
    setMonthStart(startOfMonth(new Date()));
  };
  const goMonth = (delta) => setMonthStart((d) => addMonths(d, delta));

  // Month grid — same filtering as the week view but spanning 42 cells.
  const monthDays = monthGridDays(monthStart);
  const monthLabel = monthStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const eventsByMonthDay = monthDays.map((d) =>
    filtered.filter((e) => new Date(e.start).toDateString() === d.toDateString())
  );

  return (
    <Layout
      title="Global schedule"
      subtitle="Multi-client overlay · two-way calendar sync"
      right={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView("month")}
            className={`pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 ${view === "month" ? "bg-slate-900 text-white border-slate-900" : ""}`}
            data-testid="schedule-view-month"
          ><CalendarRange size={12} /> Month</button>
          <button
            onClick={() => setView("week")}
            className={`pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 ${view === "week" ? "bg-slate-900 text-white border-slate-900" : ""}`}
            data-testid="schedule-view-week"
          ><CalendarDays size={12} /> Week</button>
          <button
            onClick={() => setView("list")}
            className={`pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 ${view === "list" ? "bg-slate-900 text-white border-slate-900" : ""}`}
            data-testid="schedule-view-list"
          ><ListIcon size={12} /> List</button>
        </div>
      }
    >
      {/* Client chip overlay legend */}
      <div className="pm-card p-3 flex flex-wrap items-center gap-2 mb-4" data-testid="schedule-client-legend">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mr-2">Overlay</span>
        {clients.map((c, idx) => {
          const on = selected.size === 0 || selected.has(c.id);
          return (
            <button
              key={c.id}
              onClick={() => toggle(c.id)}
              className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-full border text-[11px] font-bold transition-all ${on ? "border-slate-900 bg-white text-slate-900" : "border-slate-200 bg-slate-50 text-slate-400"}`}
              data-testid={`schedule-overlay-${c.id}`}
              title={on ? "Click to hide" : "Click to show"}
            >
              <span className="w-2 h-2 rounded-full" style={{ background: clientColor(idx) }} />
              {c.full_name.split(" ").slice(-1)[0]}
            </button>
          );
        })}
        <div className="ml-auto inline-flex items-center gap-2 text-[11px] text-slate-500">
          <Cable size={11} /> Google Calendar · Outlook
          <span className="pm-pill pm-pill-amber text-[10px]" data-testid="two-way-sync-pill">2-WAY SYNC (MOCKED)</span>
        </div>
      </div>

      {view === "month" && (
        <div className="pm-card p-4" data-testid="schedule-month-view">
          {/* Month toolbar */}
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => goMonth(-1)} className="pm-btn-secondary h-9 w-9 rounded-lg flex items-center justify-center" data-testid="schedule-prev-month"><ChevronLeft size={14} /></button>
            <button onClick={goToday} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="schedule-today-month">Today</button>
            <button onClick={() => goMonth(1)} className="pm-btn-secondary h-9 w-9 rounded-lg flex items-center justify-center" data-testid="schedule-next-month"><ChevronRight size={14} /></button>
            <div className="font-display text-lg font-bold tracking-tight">
              {monthLabel}
            </div>
            <span className="ml-auto text-[11px] text-slate-500 font-mono-tab" data-testid="schedule-month-count">
              {filtered.filter((e) => {
                const dt = new Date(e.start);
                return dt.getFullYear() === monthStart.getFullYear() && dt.getMonth() === monthStart.getMonth();
              }).length} events · {new Set(filtered.filter((e) => {
                const dt = new Date(e.start);
                return dt.getFullYear() === monthStart.getFullYear() && dt.getMonth() === monthStart.getMonth();
              }).map((e) => e.client.id)).size} clients
            </span>
          </div>
          {/* Day-of-week header */}
          <div className="grid grid-cols-7 gap-px text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="text-center py-1">{d}</div>
            ))}
          </div>
          {/* 7×6 grid */}
          <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-xl overflow-hidden">
            {monthDays.map((d, i) => {
              const inMonth = d.getMonth() === monthStart.getMonth();
              const isToday = d.toDateString() === new Date().toDateString();
              const dayEvents = eventsByMonthDay[i];
              return (
                <div
                  key={i}
                  className={`min-h-[110px] p-1.5 ${inMonth ? "bg-white" : "bg-slate-50/60"} ${isToday ? "ring-2 ring-emerald-400 ring-inset" : ""}`}
                  data-testid={`schedule-month-day-${i}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-mono-tab text-xs ${isToday ? "text-emerald-700 font-bold" : inMonth ? "text-slate-700" : "text-slate-300"}`}>
                      {d.getDate()}
                    </span>
                    {dayEvents.length > 2 && (
                      <span className="text-[9px] text-slate-400 font-mono-tab">+{dayEvents.length - 2}</span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 2).map((e) => {
                      const cidx = clients.findIndex((c) => c.id === e.client.id);
                      const colour = clientColor(cidx >= 0 ? cidx : 0);
                      const isMatch = e.type === "match" || e.event_type === "match";
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => {
                            setView("list");
                            requestAnimationFrame(() => {
                              const node = eventRefs.current[e.id];
                              if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
                              setHighlightId(e.id);
                              setTimeout(() => setHighlightId(null), 3500);
                            });
                          }}
                          data-testid={`schedule-month-event-${e.id}`}
                          className="w-full text-left rounded px-1 py-0.5 text-[10px] truncate flex items-center gap-1 hover:opacity-100 opacity-90 transition-opacity"
                          style={{ background: colour + "20", borderLeft: `2px solid ${colour}` }}
                          title={`${e.title} — ${e.client.full_name}`}
                        >
                          {isMatch && <span className="text-[7px]">⚽</span>}
                          <span className="font-bold text-slate-800 truncate">{e.client.full_name.split(" ").slice(-1)[0]}</span>
                          <span className="text-slate-500 ml-auto shrink-0 font-mono-tab">{new Date(e.start).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="pm-card p-4" data-testid="schedule-week-view">
          {/* Week toolbar */}
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => goWeek(-1)} className="pm-btn-secondary h-9 w-9 rounded-lg flex items-center justify-center" data-testid="schedule-prev-week"><ChevronLeft size={14} /></button>
            <button onClick={goToday} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold" data-testid="schedule-today">Today</button>
            <button onClick={() => goWeek(1)} className="pm-btn-secondary h-9 w-9 rounded-lg flex items-center justify-center" data-testid="schedule-next-week"><ChevronRight size={14} /></button>
            <div className="font-display text-lg font-bold tracking-tight">
              {weekStart.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} — {addDays(weekStart, 6).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
            <span className="ml-auto text-[11px] text-slate-500 font-mono-tab">{weekEvents.length} events · {new Set(weekEvents.map((e) => e.client.id)).size} clients</span>
          </div>
          {/* 7-column grid */}
          <div className="overflow-x-auto pm-scroll">
            <div className="min-w-[900px] grid grid-cols-7 gap-px bg-slate-200 rounded-xl overflow-hidden">
              {weekDays.map((d, i) => {
                const today = d.toDateString() === new Date().toDateString();
                const dayName = d.toLocaleDateString("en-GB", { weekday: "short" }).toUpperCase();
                return (
                  <div key={i} className={`bg-white p-3 min-h-[180px] ${today ? "ring-1 ring-emerald-400" : ""}`} data-testid={`schedule-day-${i}`}>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-400">{dayName}</span>
                      <span className={`font-mono-tab text-sm ${today ? "text-emerald-700 font-bold" : "text-slate-700"}`}>{d.getDate()}</span>
                    </div>
                    <div className="space-y-1.5">
                      {eventsByDay[i].length === 0 && <div className="text-[10px] text-slate-300">—</div>}
                      {eventsByDay[i].map((e) => {
                        const cidx = clients.findIndex((c) => c.id === e.client.id);
                        const colour = clientColor(cidx >= 0 ? cidx : 0);
                        return (
                          <button
                            key={e.id}
                            type="button"
                            onClick={() => {
                              setView("list");
                              requestAnimationFrame(() => {
                                const node = eventRefs.current[e.id];
                                if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
                                setHighlightId(e.id);
                                setTimeout(() => setHighlightId(null), 3500);
                              });
                            }}
                            data-testid={`schedule-event-${e.id}`}
                            className="w-full text-left rounded-lg px-2 py-1.5 border text-[11px] cursor-pointer"
                            style={{ borderColor: colour + "55", background: colour + "10" }}
                          >
                            <div className="font-bold text-slate-900 truncate flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: colour }} />
                              {e.title}
                            </div>
                            <div className="text-slate-500 truncate">{e.client.full_name.split(" ")[0]} · {new Date(e.start).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="pm-card p-6">
          <h3 className="font-display text-xl font-bold tracking-tight mb-4">Upcoming · {upcoming.length}</h3>
          <div className="space-y-2">
            {upcoming.map((e) => (
              <div
                key={e.id}
                ref={(node) => { if (node) eventRefs.current[e.id] = node; }}
                className={`flex items-center gap-4 px-4 py-3 rounded-xl border transition-all ${
                  highlightId === e.id
                    ? "border-emerald-400 ring-2 ring-emerald-300 bg-emerald-50/60 animate-pulse"
                    : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/60"
                }`}
                   data-testid={`global-event-${e.id}`}>
                <Link to={`/clients/${e.client.id}?tab=schedule`} className="flex items-center gap-4 flex-1 min-w-0">
                  <img src={e.client.photo_url || `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><rect width='40' height='40' fill='%23475569'/><text x='50%25' y='55%25' font-family='system-ui' font-size='14' font-weight='700' fill='white' text-anchor='middle'>${(e.client.full_name||'').split(' ').map(s=>s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()}</text></svg>`} alt={e.client.full_name} className="w-10 h-10 rounded-lg object-cover bg-slate-700" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{e.title}</div>
                    <div className="text-xs text-slate-500 truncate">{e.client.full_name} · {e.location || "—"}</div>
                  </div>
                  <span className={`pm-pill ${EVENT_COLORS[e.event_type] || "pm-pill-slate"}`}>{(e.event_type || "event").replace("_", " ")}</span>
                  <div className="font-mono-tab text-sm text-slate-700 w-44 text-right">{fmtDateTime(e.start)}</div>
                </Link>
                {e.event_type === "match" && (
                  <button
                    onClick={() => setMatchNoteFor(e)}
                    className="pm-btn-secondary h-8 px-2.5 rounded-lg text-[11px] font-bold flex items-center gap-1 shrink-0"
                    data-testid={`add-match-note-${e.id}`}
                    title="Add match note"
                  >
                    <NotebookPen size={11} /> Note
                  </button>
                )}
              </div>
            ))}
            {upcoming.length === 0 && (
              <div className="text-center py-10" data-testid="schedule-empty-state">
                <CalendarIcon className="mx-auto mb-3 text-slate-400" size={48} />
                <div className="font-display font-black text-xl tracking-tight text-slate-900 mb-1">
                  No fixtures available yet
                </div>
                <div className="text-sm text-slate-600 max-w-md mx-auto">
                  We couldn't find upcoming fixtures for your roster's clubs. This updates automatically once API-Football has new fixtures, or click <a href="/settings/integrations" className="underline">Settings → Integrations → Re-link team IDs</a> to refresh team mappings.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {matchNoteFor && (
        <MatchNoteModal
          event={matchNoteFor}
          onClose={() => setMatchNoteFor(null)}
          onSaved={() => setMatchNoteFor(null)}
        />
      )}
    </Layout>
  );
}

function parseTeamsFromTitle(title = "") {
  // Common patterns: "Ajax vs PSV", "Ajax v PSV", "Ajax - PSV"
  const m = title.match(/^(.+?)\s+(?:vs?\.?|v|-)\s+(.+)$/i);
  if (m) return { home_team: m[1].trim(), away_team: m[2].trim() };
  return { home_team: "", away_team: "" };
}

function MatchNoteModal({ event, onClose, onSaved }) {
  const { home_team, away_team } = parseTeamsFromTitle(event.title);
  const startDate = new Date(event.start);
  const initialMatch = {
    home_team,
    away_team,
    venue: event.location || "",
    match_date: startDate.toISOString().slice(0, 10),
    competition: event.competition || "",
    fixture_id: event.fixture_id || event.id,
    players_observed: event.client?.id
      ? [{ name: event.client.full_name, client_id: event.client.id, position: event.client.position || "", club: event.client.current_club || "", rating: 0, note: "" }]
      : [],
    general_notes: "",
  };
  const initialLinks = event.client?.id ? [{ entity_type: "client", entity_id: event.client.id }] : [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="match-note-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto pm-scroll">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Match note</div>
            <div className="font-display text-lg font-bold tracking-tight">{event.title}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="match-note-modal-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5">
          <NoteComposer
            type="match"
            initialMatch={initialMatch}
            initialLinks={initialLinks}
            initialTitle={event.title}
            onCancel={onClose}
            onSaved={onSaved}
            hideHeader
          />
        </div>
      </div>
    </div>
  );
}
